import { MotionText } from '@shared/atoms';
import { FreedomToSpeakUpConcernListItem } from '@shared/types/freedomToSpeakUpConcern';
import { formatDateStringSplitDots, formatDateToDayShortMonthYear } from '@utils/date.util';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { FreedomToSpeakUpMessagingModal } from '../FreedomToSpeakUp/FreedomToSpeakUpMessagingModal';
import { SystemButton } from '../SystemButton/SystemButton';
import styles from './index.module.scss';

interface TruncatedTextProps {
  text: string;
  maxLength: number;
  className: string;
  isBlockquote: boolean;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  maxLength,
  className,
  isBlockquote = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text.length <= maxLength) {
    if (isBlockquote) {
      return <blockquote className={className}>&apos;{text}&apos;</blockquote>;
    }

    return <MotionText as="p" className={className} text={text} />;
  }

  const truncatedText = text.substring(0, maxLength);
  const remainingText = text.substring(maxLength);
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  if (isBlockquote) {
    return (
      <blockquote className={className}>
        &apos;{truncatedText}
        {!isExpanded && '...'}
        {isExpanded && remainingText}&apos;
        <span
          className={styles.showMore}
          onClick={toggleExpanded}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </span>
      </blockquote>
    );
  }

  return (
    <div className={className} style={{ marginBottom: '20px' }}>
      {truncatedText}
      {!isExpanded && '...'}
      {isExpanded && remainingText}
      <span
        className={styles.showMore}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {isExpanded ? 'Show less' : 'Show more'}
      </span>
    </div>
  );
};

export interface ShareConcernCardProps {
  concern: FreedomToSpeakUpConcernListItem;
}

export const ShareConcernCard: React.FC<ShareConcernCardProps> = ({ concern }) => {
  const [messagingOpen, setMessagingOpen] = useState(false);

  const {
    id,
    referenceNumber,
    createdAt,
    subject,
    status,
    statusLabel,
    responseDate,
    responseHeading,
    response,
  } = concern;

  const submittedLabel = (() => {
    try {
      return formatDateToDayShortMonthYear(createdAt);
    } catch {
      return formatDateStringSplitDots(createdAt);
    }
  })();

  const handleViewMessage = useCallback(() => {
    setMessagingOpen(true);
  }, []);

  const handleCloseMessaging = useCallback(() => {
    setMessagingOpen(false);
  }, []);

  const resolvedResponseHeading =
    responseDate && responseHeading.startsWith('Resolved')
      ? responseHeading
      : responseDate
      ? `Response on ${formatDateStringSplitDots(responseDate)}`
      : responseHeading;

  return (
    <div className={styles.cardContainer}>
      <div className={styles.header}>
        <div className={styles.concernIconContainer}>
          <FlagOutlinedIcon className={styles.flagIcon} aria-hidden />
          <MotionText as="h3" className={styles.title} text="Concern" />
        </div>
        <div className={styles.rightSide}>
          <span
            className={classNames(styles.statusBadge, styles[`status_${status}`])}
            aria-label={`Status: ${statusLabel}`}
          >
            {statusLabel}
          </span>
        </div>
      </div>

      <MotionText
        as="h4"
        className={styles.metaLine}
        text={`${referenceNumber} • Submitted ${submittedLabel}`}
      />

      <MotionText as="p" className={styles.subjectLine} text={subject} />

      <div className={styles.responseContainer}>
        <MotionText as="h4" className={styles.responseDate} text={resolvedResponseHeading} />
        <TruncatedText
          text={response}
          maxLength={300}
          className={styles.responseText}
          isBlockquote={false}
        />
      </div>

      <div className={styles.footer}>
        <SystemButton variant="primaryDarkSkyBlue" onClick={handleViewMessage}>
          View & Message
        </SystemButton>
      </div>

      <FreedomToSpeakUpMessagingModal
        concernId={id}
        referenceNumber={referenceNumber}
        isOpen={messagingOpen}
        onClose={handleCloseMessaging}
      />
    </div>
  );
};
